<template>
    <div class="portal-card-wrapper">
        <div class="portal-card">
            <div class="portal-card__header">
                <div class="header-top">
                    <div class="header-icon-box">
                        <common-icon name="insure" size="medium"></common-icon>
                    </div>
                    <h1 class="header-title">
                        {{ $t('pages.public.record_upload.step_verify.title') }}
                    </h1>
                </div>
                <p class="header-subtitle">
                    {{ $t('pages.public.record_upload.step_verify.subtitle') }}
                </p>
            </div>

            <div class="portal-card__body">
                <div class="expiration-alert">
                    <div class="expiration-alert__icon">
                        <common-icon name="emergancy" size="small"></common-icon>
                    </div>
                    <div class="expiration-alert__text">
                        <span>{{ $t('pages.public.record_upload.step_verify.link_expires_on') }}</span>
                        <strong>{{ expirationDate }}.</strong>
                        <span>{{ $t('pages.public.record_upload.step_verify.security_warning') }}</span>
                    </div>
                </div>

                <div class="access-code-section">
                    <label class="access-code-label">
                        {{ $t('pages.public.record_upload.step_verify.code_label') }}
                    </label>
                    <div class="access-code-inputs">
                        <input
                            v-for="(digit, idx) in 4"
                            :key="idx"
                            :ref="'codeBox' + idx"
                            v-model="codeDigits[idx]"
                            type="text"
                            maxlength="1"
                            class="code-box"
                            :class="{ 'code-box--filled': codeDigits[idx], 'code-box--error': verifyError }"
                            autocomplete="off"
                            @input="onCodeInput(idx, $event)"
                            @keydown="onCodeKeydown(idx, $event)"
                            @paste="onCodePaste($event)"
                        />
                    </div>
                    <p class="access-code-hint">
                        {{ $t('pages.public.record_upload.step_verify.code_hint') }}
                        <span class="hint-highlight">{{ subjectReference }}</span>
                    </p>
                    <p v-if="verifyError" class="verify-error-msg">
                        {{ verifyError }}
                    </p>
                </div>

                <div class="request-overview">
                    <h4 class="request-overview__title">
                        {{ $t('pages.public.record_upload.step_verify.overview_title') }}
                    </h4>
                    <div class="request-overview__grid">
                        <div class="overview-item">
                            <div class="overview-item__icon">
                                <common-icon name="user" size="extra-small"></common-icon>
                            </div>
                            <div class="overview-item__content">
                                <span class="overview-label">
                                    {{ $t('pages.public.record_upload.step_verify.patient_label') }}
                                </span>
                                <strong class="overview-value">{{ patientName }}</strong>
                            </div>
                        </div>

                        <div class="overview-item">
                            <div class="overview-item__icon">
                                <common-icon name="file" size="extra-small"></common-icon>
                            </div>
                            <div class="overview-item__content">
                                <span class="overview-label">
                                    {{ $t('pages.public.record_upload.step_verify.due_label') }}
                                </span>
                                <strong class="overview-value">{{ dueDate }}</strong>
                            </div>
                        </div>

                        <div class="overview-item overview-item--full">
                            <div class="overview-item__icon">
                                <common-icon name="timeline" size="extra-small"></common-icon>
                            </div>
                            <div class="overview-item__content">
                                <span class="overview-label">
                                    {{ $t('pages.public.record_upload.step_verify.records_label') }}
                                </span>
                                <strong class="overview-value">{{ formattedRecordTypes }}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="action-section">
                    <button class="btn-primary" :disabled="!isCodeComplete || isVerifying" @click="handleVerify">
                        <common-icon name="insure" size="medium"></common-icon>
                        <span>{{ verifyButtonText }}</span>
                    </button>
                </div>

                <div class="support-footer">
                    {{ $t('pages.public.record_upload.step_verify.support_prompt') }}
                    <a href="tel:8005550190">(800) 555-0190</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'step-verify',
        props: {
            patientName: { type: String, default: 'Amos David' },
            dueDate: { type: String, default: '2/17/2025' },
            expirationDate: { type: String, default: '2/14/2025' },
            subjectReference: { type: String, default: 'MMN-7X2K-9P4R' },
            recordTypes: {
                type: Array,
                default: () => ['Complete Medical History', 'Lab Results'],
            },
            isVerifying: { type: Boolean, default: false },
            verifyError: { type: String, default: '' },
        },
        data() {
            return {
                codeDigits: ['', '', '', ''],
            }
        },
        computed: {
            isCodeComplete() {
                return this.codeDigits.every((d) => d && d.trim().length === 1)
            },
            formattedRecordTypes() {
                return Array.isArray(this.recordTypes) ? this.recordTypes.join(', ') : ''
            },
            verifyButtonText() {
                return this.isVerifying
                    ? this.$t('pages.public.record_upload.step_verify.verifying')
                    : this.$t('pages.public.record_upload.step_verify.verify_continue')
            },
        },
        methods: {
            onCodeInput(index, event) {
                const value = event.target.value.toUpperCase()
                this.$set(this.codeDigits, index, value.slice(-1))
                this.$emit('clear-error')

                if (value && index < 3) {
                    const nextBox = this.$refs['codeBox' + (index + 1)]
                    if (nextBox && nextBox[0]) {
                        nextBox[0].focus()
                    }
                }
            },
            onCodeKeydown(index, event) {
                if (event.key === 'Backspace' && !this.codeDigits[index] && index > 0) {
                    const prevBox = this.$refs['codeBox' + (index - 1)]
                    if (prevBox && prevBox[0]) {
                        prevBox[0].focus()
                    }
                }
            },
            onCodePaste(event) {
                event.preventDefault()
                const pastedData = (event.clipboardData || window.clipboardData)
                    .getData('text')
                    .trim()
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/gi, '')

                if (pastedData) {
                    for (let i = 0; i < 4; i++) {
                        this.$set(this.codeDigits, i, pastedData[i] || '')
                    }
                    const focusIndex = Math.min(pastedData.length, 3)
                    const targetBox = this.$refs['codeBox' + focusIndex]
                    if (targetBox && targetBox[0]) {
                        targetBox[0].focus()
                    }
                }
            },
            handleVerify() {
                this.$emit('verify', this.codeDigits.join(''))
            },
        },
        mounted() {
            this.$nextTick(() => {
                const firstBox = this.$refs.codeBox0
                if (firstBox && firstBox[0]) {
                    firstBox[0].focus()
                }
            })
        },
    }
</script>

<style scoped lang="scss">
    $brand-primary: #155dfc;
    $brand-primary-hover: #1048c7;
    $brand-slate: #334155;
    $brand-slate-light: #64748b;
    $brand-border: #e2e8f0;
    $alert-amber-bg: #fffdf0;
    $alert-amber-border: #fef08a;

    .portal-card-wrapper {
        width: 100%;
    }

    .portal-card {
        background: #ffffff;
        border-radius: 16px;
        border: 1px solid $brand-border;
        box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05);
        overflow: hidden;

        &__header {
            background: $brand-primary;
            padding: 24px 28px;
            color: #ffffff;
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        &__body {
            padding: 28px;
        }
    }

    .header-top {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .header-icon-box {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        i,
        svg {
            color: #ffffff;
            fill: #ffffff;
        }
    }

    .header-title {
        font-size: 18px;
        font-weight: 700;
        margin: 0;
        color: #ffffff;
        line-height: 1.3;
    }

    .header-subtitle {
        font-size: 13px;
        margin: 0;
        color: rgba(255, 255, 255, 0.9);
        line-height: 1.45;
    }

    /* Amber Alert */
    .expiration-alert {
        background-color: $alert-amber-bg;
        border: 1px solid $alert-amber-border;
        border-radius: 8px;
        padding: 14px 16px;
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin-bottom: 24px;

        &__icon {
            color: #92400e;
        }

        &__text {
            font-size: 13px;
            line-height: 1.45;
            color: #92400e;
            flex-direction: column;
            display: flex;
            strong {
                font-weight: 700;
                color: #78350f;
            }
        }
    }

    /* Access Code Inputs */
    .access-code-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 24px;
    }

    .access-code-label {
        font-size: 14px;
        font-weight: 600;
        color: #1e293b;
        margin-bottom: 14px;
        text-align: center;
    }

    .access-code-inputs {
        display: flex;
        gap: 14px;
        justify-content: center;
    }

    .code-box {
        width: 52px;
        height: 56px;
        border: 2px solid #94a3b8;
        border-radius: 12px;
        background: #ffffff;
        font-size: 24px;
        font-weight: 700;
        text-align: center;
        text-transform: uppercase;
        color: #0f172a;
        outline: none;
        transition: all 0.2s ease;

        &:focus {
            border-color: $brand-primary;
            box-shadow: 0 0 0 3px rgba(21, 93, 252, 0.18);
        }

        &--filled {
            border-color: $brand-primary;
            background: #eff6ff;
            color: $brand-primary;
        }

        &--error {
            border-color: #ef4444;
            background: #fef2f2;
            color: #ef4444;
        }
    }

    .access-code-hint {
        font-size: 12px;
        color: $brand-slate-light;
        margin-top: 12px;
        margin-bottom: 0;

        .hint-highlight {
            font-weight: 700;
            color: #1e293b;
        }
    }

    .verify-error-msg {
        color: #ef4444;
        font-size: 12px;
        font-weight: 500;
        margin-top: 8px;
    }

    /* Request Overview */
    .request-overview {
        border-top: 1px solid $brand-border;
        padding-top: 18px;
        margin-bottom: 24px;

        &__title {
            font-size: 13px;
            font-weight: 600;
            color: $brand-slate;
            margin: 0 0 12px;
        }

        &__grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
        }
    }

    .overview-item {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        font-size: 13px;

        &--full {
            grid-column: span 2;
        }

        &__icon {
            color: #99a1af;
        }

        &__content {
            line-height: 1.4;
        }

        .overview-label {
            color: $brand-slate-light;
            margin-right: 4px;
        }

        .overview-value {
            color: #0f172a;
            font-weight: 600;
        }
    }

    .action-section {
        margin-bottom: 16px;
    }

    .btn-primary {
        width: 100%;
        background-color: $brand-primary;
        color: #ffffff;
        border: none;
        border-radius: 8px;
        padding: 12px 18px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: all 0.2s ease;

        &:hover:not(:disabled) {
            background-color: $brand-primary-hover;
            transform: translateY(-1px);
        }

        &:disabled {
            opacity: 0.55;
            cursor: not-allowed;
        }
    }

    .support-footer {
        text-align: center;
        font-size: 12px;
        color: $brand-slate-light;
        margin-top: 16px;

        a {
            color: $brand-primary;
            text-decoration: none;
            font-weight: 500;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    @media (max-width: 640px) {
        .request-overview__grid {
            grid-template-columns: 1fr;
        }
        .overview-item--full {
            grid-column: span 1;
        }
    }
</style>
